import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { printService } from 'api/services/printService';
import { PRINT_CODE_PAGES } from 'api/types';
import type { Printer, PrintAgent } from 'api/types';

/**
 * Add / edit a printer (architecture section 17, printer form). Phase 1 prints
 * over the network in text mode: USB (an OS printer) and image rendering are
 * shown, disabled, so nobody wonders where they went.
 */
interface Props {
  isOpen: boolean;
  mode: 'create' | 'edit';
  record?: Printer | null;
  /** Agents a printer can sit on: every agent not revoked. */
  agents: PrintAgent[];
  onClose: () => void;
  onSaved: () => void;
}

interface FormData {
  agentId: string; code: string; name: string; host: string; port: string; paper: '58' | '80';
  charsPerLine: string; codePage: string; hasCutter: boolean; hasBuzzer: boolean; supportsStatus: boolean; isActive: boolean;
}
const EMPTY: FormData = {
  agentId: '', code: '', name: '', host: '', port: '9100', paper: '80', charsPerLine: '', codePage: 'PC437',
  hasCutter: true, hasBuzzer: false, supportsStatus: false, isActive: true,
};

const IPV4 = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;
const HOST = /^(?=.{1,253}$)([A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?)(\.[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?)*$/;

const PrinterFormModal: React.FC<Props> = ({ isOpen, mode, record, agents, onClose, onSaved }) => {
  const [formData, setFormData] = useState<FormData>(EMPTY);
  const snapshot = useRef('');
  const recordRef = useRef(record);
  recordRef.current = record;
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const r = recordRef.current;
    const data: FormData = mode === 'edit' && r
      ? {
        agentId: r.agentId, code: r.printerCode, name: r.printerName, host: r.host ?? '', port: String(r.port ?? 9100),
        paper: r.paperWidthMm === 58 ? '58' : '80', charsPerLine: r.charsPerLine ? String(r.charsPerLine) : '', codePage: r.codePage || 'PC437',
        hasCutter: r.hasCutter, hasBuzzer: r.hasBuzzer, supportsStatus: r.supportsStatus, isActive: r.isActive,
      }
      : { ...EMPTY, agentId: agents.length === 1 ? agents[0].id : '' };
    setFormData(data);
    snapshot.current = JSON.stringify(data);
    setFieldErrors({});
    setServerError(null);
  }, [isOpen, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const isDirty = useMemo(() => !!snapshot.current && JSON.stringify(formData) !== snapshot.current, [formData]);

  const set = <K extends keyof FormData>(key: K, value: FormData[K]) => {
    setFieldErrors((p) => { if (!p[key as string]) return p; const n = { ...p }; delete n[key as string]; return n; });
    setFormData((p) => ({ ...p, [key]: value }));
  };

  const agentOptions = useMemo(() => {
    const opts = agents.map((a) => ({
      value: a.id,
      label: `${a.agentName}${a.siteName ? ` · ${a.siteName}` : ''}${a.agentStatus !== 'ACTIVE' ? ` (${a.agentStatus === 'PENDING_APPROVAL' ? 'waiting for approval' : a.agentStatus.toLowerCase()})` : ''}`,
    }));
    // A printer on a revoked agent still shows its agent, so the field is not blank.
    if (record && !agents.some((a) => a.id === record.agentId)) opts.unshift({ value: record.agentId, label: `${record.agentName ?? 'Agent'} (revoked)` });
    return opts;
  }, [agents, record]);

  const handleClose = useCallback(() => { if (isDirty) setDiscardConfirmOpen(true); else onClose(); }, [isDirty, onClose]);

  const handleSave = useCallback(() => {
    const e: Record<string, string> = {};
    if (!formData.agentId) e.agentId = 'Pick the Print Agent that reaches this printer.';
    if (!/^[A-Za-z0-9_-]{1,20}$/.test(formData.code.trim())) e.code = 'The code is 1 to 20 letters, digits, - or _, e.g. KIT1.';
    const n = formData.name.trim();
    if (n.length < 2 || n.length > 60) e.name = 'The name is 2 to 60 characters, e.g. Main kitchen.';
    const h = formData.host.trim();
    if (!h) e.host = 'The printer\'s IP address or host name, e.g. 192.168.1.50.';
    else if (!IPV4.test(h) && !(HOST.test(h) && /[A-Za-z]/.test(h))) e.host = 'Not an IPv4 address or host name.';
    const port = Number(formData.port);
    if (!Number.isInteger(port) || port < 1 || port > 65535) e.port = '1 to 65535. Most receipt printers use 9100.';
    if (formData.charsPerLine.trim()) {
      const c = Number(formData.charsPerLine);
      if (!Number.isInteger(c) || c < 24 || c > 64) e.charsPerLine = 'A whole number from 24 to 64, or blank for the paper default.';
    }
    if (Object.keys(e).length) {
      setFieldErrors(e);
      requestAnimationFrame(() => document.querySelector<HTMLElement>(`[data-field="${Object.keys(e)[0]}"]`)?.scrollIntoView({ block: 'center' }));
      return;
    }
    setSaveConfirmOpen(true);
  }, [formData]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    setServerError(null);
    const body = {
      agentId: formData.agentId,
      printerCode: formData.code.trim().toUpperCase(),
      printerName: formData.name.trim(),
      connectionType: 'NETWORK' as const,
      host: formData.host.trim(),
      port: Number(formData.port),
      paperWidthMm: Number(formData.paper),
      charsPerLine: formData.charsPerLine.trim() ? Number(formData.charsPerLine) : undefined,
      codePage: formData.codePage,
      renderMode: 'TEXT' as const,
      hasCutter: formData.hasCutter,
      hasBuzzer: formData.hasBuzzer,
      supportsStatus: formData.supportsStatus,
    };
    try {
      if (mode === 'edit' && record) {
        await printService.updatePrinter(record.id, { ...body, isActive: formData.isActive, rowVersion: record.rowVersion });
        notificationService.toast.success(`"${body.printerName}" updated.`);
      } else {
        await printService.createPrinter(body);
        notificationService.toast.success(`"${body.printerName}" added. Print a test page from its row to check it.`);
      }
      setIsSaving(false);
      onSaved();
      onClose();
    } catch (err: any) {
      setIsSaving(false);
      setServerError(err?.response?.data?.message ?? 'That could not be saved.');
    }
  }, [formData, mode, record, onSaved, onClose]);

  if (!isOpen) return null;
  const err = (k: string) => fieldErrors[k];
  const hint = (t: string) => <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{t}</div>;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md" role="dialog" aria-modal="true" style={{ minHeight: 'auto', maxWidth: 680 }} data-printer-form>
        {isSaving && (
          <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit' }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Saving...</span>
          </div>
        )}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">{mode === 'create' ? 'Add Printer' : `Edit Printer — ${record?.printerName ?? ''}`}</h2>
          <button className="modal-close" onClick={handleClose} disabled={isSaving} aria-label="Close"><IconSystem.x size={20} /></button>
        </div>
        <div className="modal-content">
          {serverError && <div className="form-error-message" style={{ marginBottom: 12 }} data-server-error>{serverError}</div>}

          <div className="form-group" data-field="agentId">
            <SyutSelect label={<>Print agent <span className="text-danger">*</span></>} options={agentOptions} value={formData.agentId}
              onChange={(v) => set('agentId', (v as string) || '')} placeholder={agents.length ? 'Pick an agent' : 'No agent yet — pair one on Print Agents'}
              error={err('agentId')} size="sm" isSearchable isClearable={false} menuPortalTarget={document.body} />
            {hint('The agent is the PC on the same network as the printer. It sends the tickets to it.')}
          </div>

          <div className="form-grid-2">
            <div className="form-group" data-field="code">
              <label className="form-label" htmlFor="prn-code">Code <span className="text-danger">*</span></label>
              <input id="prn-code" className={`form-input${err('code') ? ' form-field-error' : ''}`} value={formData.code} maxLength={20}
                onChange={(e) => set('code', e.target.value.toUpperCase())} placeholder="e.g., KIT1" />
              {err('code') && <div className="form-error-message">{err('code')}</div>}
            </div>
            <div className="form-group" data-field="name">
              <label className="form-label" htmlFor="prn-name">Printer name <span className="text-danger">*</span></label>
              <input id="prn-name" className={`form-input${err('name') ? ' form-field-error' : ''}`} value={formData.name} maxLength={60}
                onChange={(e) => set('name', e.target.value)} placeholder="e.g., Main kitchen" />
              {err('name') && <div className="form-error-message">{err('name')}</div>}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Connection <span className="text-danger">*</span></label>
            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="radio" name="prn-conn" checked readOnly /> Network (IP address)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-muted)' }} title="Phase 2">
                <input type="radio" name="prn-conn" disabled /> USB (Windows / CUPS printer) <em style={{ fontSize: 12 }}>— phase 2</em>
              </label>
            </div>
          </div>

          <div className="form-grid-2">
            <div className="form-group" data-field="host">
              <label className="form-label" htmlFor="prn-host">IP address or host name <span className="text-danger">*</span></label>
              <input id="prn-host" className={`form-input${err('host') ? ' form-field-error' : ''}`} value={formData.host} maxLength={253}
                onChange={(e) => set('host', e.target.value)} placeholder="e.g., 192.168.1.50" />
              {err('host') && <div className="form-error-message">{err('host')}</div>}
            </div>
            <div className="form-group" data-field="port">
              <label className="form-label" htmlFor="prn-port">Port <span className="text-danger">*</span></label>
              <input id="prn-port" type="number" min={1} max={65535} className={`form-input${err('port') ? ' form-field-error' : ''}`} value={formData.port}
                onChange={(e) => set('port', e.target.value)} />
              {err('port') && <div className="form-error-message">{err('port')}</div>}
            </div>
          </div>

          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">Paper width <span className="text-danger">*</span></label>
              <div style={{ display: 'flex', gap: 18 }}>
                {(['80', '58'] as const).map((w) => (
                  <label key={w} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input type="radio" name="prn-paper" value={w} checked={formData.paper === w} onChange={() => set('paper', w)} /> {w} mm
                  </label>
                ))}
              </div>
            </div>
            <div className="form-group" data-field="charsPerLine">
              <label className="form-label" htmlFor="prn-cpl">Characters per line</label>
              <input id="prn-cpl" type="number" className={`form-input${err('charsPerLine') ? ' form-field-error' : ''}`} value={formData.charsPerLine}
                onChange={(e) => set('charsPerLine', e.target.value)} placeholder={formData.paper === '58' ? '32 (default)' : '48 (default)'} />
              {err('charsPerLine') && <div className="form-error-message">{err('charsPerLine')}</div>}
            </div>
          </div>

          <div className="form-grid-2">
            <div className="form-group">
              <SyutSelect label={<>Print text as <span className="text-danger">*</span></>} size="sm" value="TEXT" isClearable={false} menuPortalTarget={document.body}
                options={[{ value: 'TEXT', label: 'Text (fast)' }, { value: 'IMAGE', label: 'Image (local scripts) — phase 2', isDisabled: true }]}
                onChange={() => undefined} />
            </div>
            <div className="form-group" data-field="codePage">
              <SyutSelect label={<>Character set <span className="text-danger">*</span></>} size="sm" value={formData.codePage} isClearable={false} menuPortalTarget={document.body}
                options={PRINT_CODE_PAGES.map((c) => ({ value: c, label: c === 'PC437' ? 'PC437 (default)' : c }))}
                onChange={(v) => set('codePage', (v as string) || 'PC437')} />
              {hint('The set the printer supports. Characters outside it print as ?.')}
            </div>
          </div>

          <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={formData.hasCutter} onChange={(e) => set('hasCutter', e.target.checked)} /> Auto-cutter
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={formData.hasBuzzer} onChange={(e) => set('hasBuzzer', e.target.checked)} /> Buzzer
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={formData.supportsStatus} onChange={(e) => set('supportsStatus', e.target.checked)} /> Reports paper / cover status
            </label>
            {mode === 'edit' && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={formData.isActive} onChange={(e) => set('isActive', e.target.checked)} /> Active
              </label>
            )}
          </div>
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={isSaving || (mode === 'edit' && !isDirty)}
            title={mode === 'edit' && !isDirty ? 'No changes to save' : undefined}>Save</button>
        </div>
      </div>
      <ConfirmDialog open={saveConfirmOpen} title="Confirm Save"
        message={mode === 'create' ? `Add the printer "${formData.name.trim()}"?` : `Save the changes to "${formData.name.trim()}"?`}
        confirmLabel="Confirm" cancelLabel="Cancel" variant="default" onConfirm={() => void handleConfirmSave()} onCancel={() => setSaveConfirmOpen(false)} />
      <ConfirmDialog open={discardConfirmOpen} title="Discard Changes?" message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard" cancelLabel="Keep Editing" variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }} onCancel={() => setDiscardConfirmOpen(false)} />
    </>
  );
};

export default PrinterFormModal;
